import { useState } from "react";
import {
  Box, Typography, Paper, Stack, TextField, MenuItem, Button, Alert, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxDispatchChannel, type DxDispatchDocType } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { DiagnosticOrderIdField } from "@/components/DiagnosticOrderIdField";
import { formatDateTime } from "@/shared/datetime";

export function DispatchPage() {
  const qc = useQueryClient();
  const [orderId, setOrderId] = useState("");
  const [docType, setDocType] = useState<DxDispatchDocType>("LabResult");
  const [docId, setDocId] = useState("");
  const [channel, setChannel] = useState<DxDispatchChannel>("Email");
  const [recipient, setRecipient] = useState("");

  const list = useQuery({ queryKey: ["dx-dispatches", orderId], queryFn: () => diagnosticsApi.listDispatches(orderId), enabled: !!orderId });

  const create = useMutation({
    meta: { successMessage: "Document dispatched" },
    mutationFn: () => diagnosticsApi.dispatch({ diagnosticOrderId: orderId, documentType: docType, documentId: docId, channel, recipient: recipient || null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dx-dispatches", orderId] }),
  });

  return (
    <Box>
      <PageHeader breadcrumbs={["Diagnostics"]} title="Delivery / dispatch" />
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" flexWrap="wrap">
          <DiagnosticOrderIdField value={orderId} onChange={setOrderId} required sx={{ minWidth: 320 }} />
          <TextField select label="Document" size="small" value={docType} onChange={e => setDocType(e.target.value as DxDispatchDocType)} sx={{ width: 170 }}>
            <MenuItem value="LabResult">Lab result</MenuItem><MenuItem value="RadiologyReport">Radiology report</MenuItem>
          </TextField>
          <TextField label="Document Id" size="small" value={docId} onChange={e => setDocId(e.target.value)} sx={{ minWidth: 260 }} />
          <TextField select label="Channel" size="small" value={channel} onChange={e => setChannel(e.target.value as DxDispatchChannel)} sx={{ width: 130 }}>
            {["Print", "Email", "Sms", "WhatsApp", "Portal", "Ehr"].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField label="Recipient" size="small" value={recipient} onChange={e => setRecipient(e.target.value)} sx={{ minWidth: 200 }} />
          <Button variant="contained" onClick={() => create.mutate()} disabled={create.isPending || !orderId || !docId}>Dispatch</Button>
        </Stack>
        {create.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(create.error)}</Alert>}
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Dispatch log {orderId ? "" : "(enter an order Id above)"}</Typography>
        {list.isError && <Alert severity="error">{errorMessage(list.error)}</Alert>}
        <Table size="small">
          <TableHead><TableRow><TableCell>Document</TableCell><TableCell>Channel</TableCell><TableCell>Recipient</TableCell><TableCell>Status</TableCell><TableCell>Dispatched</TableCell></TableRow></TableHead>
          <TableBody>
            {(list.data ?? []).map(d => (
              <TableRow key={d.id}>
                <TableCell>{d.documentType}</TableCell>
                <TableCell>{d.channel}</TableCell>
                <TableCell>{d.recipient}</TableCell>
                <TableCell><Chip size="small" label={d.status} color={d.status === "Sent" ? "success" : d.status === "Failed" ? "error" : "default"} /></TableCell>
                <TableCell>{d.dispatchedAt ? formatDateTime(d.dispatchedAt) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
